import { MappedEntity, ScenariosRequest, ScenariosResponse } from '@api';
import { deepEqual } from '@helpers';
import {
  isCancelledError,
  type QueryClient,
  type QueryKey,
  useQueryClient,
} from '@tanstack/react-query';
import { httpCode } from '@terminal/utils/api-fetch/httpCode';
import { useScenarioStore } from '@terminal/widgets/scenario/hooks/use-scenario-store/useScenarioStore';
import { isSameDay } from 'date-fns';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  createScenariosQueryKey,
  createScenariosQueryOptions,
} from './useScenarios.utils';

export interface ScenariosData extends Omit<ScenariosResponse, 'entity'> {
  entity: MappedEntity;
}

const isUsageLimitError = (error: unknown) =>
  typeof error === 'object' &&
  error !== null &&
  'status' in error &&
  error.status === httpCode.tooManyRequests;

const isAbortError = (error: unknown) =>
  typeof error === 'object' &&
  error !== null &&
  'name' in error &&
  error.name === 'AbortError';

const shouldIgnoreRequestError = ({
  activeBody,
  body,
  error,
}: {
  activeBody?: ScenariosRequest;
  body: ScenariosRequest;
  error: unknown;
}) => activeBody !== body || isCancelledError(error) || isAbortError(error);

const getCompletedFreshCachedData = ({
  queryClient,
  queryKey,
}: {
  queryClient: QueryClient;
  queryKey: QueryKey;
}) => {
  const query = queryClient.getQueryCache().find({ queryKey, exact: true });

  if (
    query?.state.status !== 'success' ||
    query.state.fetchStatus !== 'idle' ||
    query.state.isInvalidated ||
    !isSameDay(query.state.dataUpdatedAt, Date.now())
  ) {
    return undefined;
  }

  return query.state.data as ScenariosData[];
};

const handleScenarioRequestError = ({
  activeBody,
  body,
  error,
  setError,
  setHasExceededUsageLimit,
}: {
  activeBody?: ScenariosRequest;
  body: ScenariosRequest;
  error: unknown;
  setError: (value: boolean) => void;
  setHasExceededUsageLimit: (value: boolean) => void;
}) => {
  if (shouldIgnoreRequestError({ activeBody, body, error })) {
    return;
  }

  if (isUsageLimitError(error)) {
    setHasExceededUsageLimit(true);
  }

  setError(true);
};

export type UseScenariosReturn = ReturnType<typeof useScenarios>;

export const useScenarios = () => {
  const [data, setData] = useState<ScenariosData[]>([]);
  const [lastBody, setLastBody] = useState<ScenariosRequest>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const activeBodyRef = useRef<ScenariosRequest | undefined>(undefined);
  const activeQueryKeyRef = useRef<QueryKey | undefined>(undefined);
  const queryClient = useQueryClient();
  const setLastScenariosData = useScenarioStore(
    state => state.setLastScenariosData
  );
  const setHasExceededUsageLimit = useScenarioStore(
    state => state.setHasExceededUsageLimit
  );

  useEffect(
    () =>
      queryClient.getQueryCache().subscribe(event => {
        const activeQueryKey = activeQueryKeyRef.current;

        if (!activeQueryKey) {
          return;
        }

        if (!deepEqual(event.query.queryKey, activeQueryKey)) {
          return;
        }

        const streamedData = event.query.state.data;

        if (!Array.isArray(streamedData)) {
          return;
        }

        setData(streamedData);
      }),
    [queryClient]
  );

  const sendMessage = useCallback(
    async (body: ScenariosRequest) => {
      const queryKey = createScenariosQueryKey(body);
      const activeQueryKey = activeQueryKeyRef.current;
      const isEquivalentQueryActive =
        !!activeQueryKey && deepEqual(activeQueryKey, queryKey);

      if (activeQueryKey && !isEquivalentQueryActive) {
        await queryClient.cancelQueries({
          queryKey: activeQueryKey,
          exact: true,
        });
      }

      activeBodyRef.current = body;
      activeQueryKeyRef.current = queryKey;
      setError(false);
      setLastBody(body);

      const queryOptions = createScenariosQueryOptions({
        body,
        queryClient,
      });
      const cachedData = getCompletedFreshCachedData({
        queryClient,
        queryKey,
      });

      if (cachedData) {
        activeBodyRef.current = undefined;
        activeQueryKeyRef.current = undefined;
        setData(cachedData);
        setLastScenariosData(cachedData);
        setHasExceededUsageLimit(false);
        setIsLoading(false);
        return;
      }

      if (!isEquivalentQueryActive) {
        setData([]);
      }
      setIsLoading(true);

      try {
        const nextData = await queryClient.fetchQuery(queryOptions);

        if (activeBodyRef.current !== body) {
          return;
        }

        setData(nextData);
        setLastScenariosData(nextData);
        setError(false);
        setHasExceededUsageLimit(false);
      } catch (requestError) {
        handleScenarioRequestError({
          activeBody: activeBodyRef.current,
          body,
          error: requestError,
          setError,
          setHasExceededUsageLimit,
        });
      } finally {
        if (activeBodyRef.current === body) {
          activeBodyRef.current = undefined;
          activeQueryKeyRef.current = undefined;
          setIsLoading(false);
        }
      }
    },
    [queryClient, setHasExceededUsageLimit, setLastScenariosData]
  );

  const refetch = useCallback(() => {
    if (lastBody) {
      sendMessage(lastBody);
    }
  }, [lastBody, sendMessage]);

  const clear = useCallback(() => {
    setData([]);
    setLastBody(undefined);
  }, []);

  const abort = useCallback(() => {
    const activeQueryKey = activeQueryKeyRef.current;
    activeBodyRef.current = undefined;
    activeQueryKeyRef.current = undefined;

    if (activeQueryKey) {
      queryClient.cancelQueries({
        queryKey: activeQueryKey,
        exact: true,
      });
    }

    setError(false);
    setIsLoading(false);
  }, [queryClient]);

  return {
    isLoading,
    error,
    sendMessage,
    refetch,
    abort,
    clear,
    data,
  };
};
